import * as c from 'constants/integrations';
import { modalPages } from 'constants/modalPage';
import { openModalPage, previousModalPage } from 'actions/modalPage';
import { setPayment, processPayments, deletePayment } from 'actions/Payments';
import { addError } from 'actions/Error';
import { getActivePaymentIntegration } from 'reducers/cafaConfigs';
import { getEnabledPlugins, getPluginHooksByName } from 'reducers/Plugins';
import * as paymentIntegrations from 'paymentIntegrations/paymentIntegrations';
import { chainPromises } from 'utils';
import i18n from 'containers/App/i18n';

/** @todo Document */
// TODO: NOTE that processPayment does not receive props but openTerminalIntegration does, this might cause issues with some integrations
export const openTerminalIntegration = (...params) =>
  processPayments(...params);

export const setIntegrationType = integration => {
  return async dispatch => {
    dispatch({
      type: c.SET_INTEGRATION_TYPE,
      payload: integration,
    });
  };
};

/** @todo Document */
export const setIntegrationStatus = status => {
  return async dispatch => {
    dispatch({
      type: c.SET_INTEGRATION_STATUS,
      payload: status,
    });
  };
};

const defaultCloseBatch = () => async () => true;
const defaultInitPayment = ({
  cardPayments,
  resolvePayments,
}) => async dispatch =>
  Promise.all(
    cardPayments
      .filter(pmt => pmt.type === 'CARD')
      .reduce(
        (prev, pmt) =>
          prev.then(() =>
            dispatch(
              setPayment({
                ...pmt,
                paid: true,
                forceAmount: true,
              }),
            ),
          ),
        Promise.resolve(null),
      ),
  ).then(resolvePayments);
const defaultVoidPayment = ({ cardPayments }) => async dispatch =>
  Promise.all(
    cardPayments
      .filter(pmt => pmt.type === 'CARD')
      .reduce(
        (prev, pmt) =>
          prev.then(() => dispatch(deletePayment({ key: pmt.key }))),
        Promise.resolve(null),
      ),
  );
const missingAction = (action, integration) => () => async () => {
  console.warn(`Missing function '${action}' in integration '${integration}'`);
};

const getIntegration = integration => async (dispatch, getState) => {
  const defaultInt = {
    initPayments: defaultInitPayment,
    voidPayments: defaultVoidPayment,
  };
  const builtin = paymentIntegrations[integration];
  const plugin = getPluginHooksByName('customPaymentIntegration')(
    getState(),
  ).find(int => int.id === integration);

  if (integration === 'none' || integration === '')
    return Promise.resolve(defaultInt);
  if (!integration) return Promise.resolve(defaultInt);
  if (builtin) return builtin;
  if (plugin) return plugin;
  dispatch(
    addError(
      `Failed to load integration ${i18n.t('payment:integrations.name', {
        context: integration,
      })}, please reselect a payment integration from settings→payment`,
    ),
  );
  throw new Error(`Failed to load integration ${integration}`);
};
export const processWithIntegration = (
  integration,
  payments,
) => async dispatch => {
  // 'none' special-cased so that it would not open and close a modal page
  // Ideally in the future we would support this in general, by having integrations open their own modalpage or by having some prop they use to signify they don't need one
  // integration.initPaymentSolo(cardPayments) or something
  if (integration === 'none') {
    // If there's a CARD payment with integration 'none', need to actually mark it as 'paid'
    const actualCardPayments = payments.filter(p => p.type === 'CARD');
    return actualCardPayments.length
      ? actualCardPayments.reduce(
          (prev, pmt) =>
            prev.then(() =>
              dispatch(
                setPayment({
                  ...pmt,
                  paid: true,
                  forceAmount: true,
                }),
              ),
            ),
          Promise.resolve(null),
        )
      : Promise.resolve(null);
  }
  return dispatch(getIntegration(integration)).then(data =>
    new Promise((resolve, reject) =>
      dispatch(
        openModalPage({
          component: data.customComponent || modalPages.cardPaymentUI,
          isPopup: true,
          modalClassName: 'card-payment-ui',
          props: {
            integration: data,
            payments,
            resolvePayments: resolve,
            rejectPayments: reject,
          },
          groupID: modalPages.cardPaymentUI,
          replace: false,
          type: 'POS-cardPaymentUI',
        }),
      ),
    ).finally(() =>
      dispatch(
        previousModalPage({
          component: data.customComponent || modalPages.cardPaymentUI,
        }),
      ),
    ),
  );
};

export const voidWithIntegration = (integration, payments) => async (
  dispatch,
  getState,
) => {
  if (integration === 'none') {
    return chainPromises(
      ...payments.map(pmt => () =>
        dispatch(setPayment({ ...pmt, paid: false })),
      ),
    );
  }
  return dispatch(getIntegration(integration)).then(data =>
    dispatch(data.voidPayments({ cardPayments: payments })),
  );
};
const doWithIntegration = (action, defaultFunc) => {
  return (...params) => async (dispatch, getState) => {
    const currentIntegration = getActivePaymentIntegration(getState());
    const fallback = defaultFunc || missingAction(action, currentIntegration);

    const builtin = paymentIntegrations[currentIntegration];
    const plugin = getPluginHooksByName('customPaymentIntegration')(
      getState(),
    ).find(int => int.id === currentIntegration);
    if (builtin?.[action]) return dispatch(builtin[action](...params));
    if (plugin?.[action]) return dispatch(plugin[action](...params));

    console.warn(
      `Integration '${currentIntegration}' has missing or broken method '${action}'\n falling back to default behaviour\n`,
    );
    return dispatch(fallback(...params));
  };
};

export const integrationFunctions = () => async (dispatch, getState) => {
  const currentIntegration = getActivePaymentIntegration(getState());
  const builtin = paymentIntegrations[currentIntegration];
  const plugin = getPluginHooksByName('customPaymentIntegration')(
    getState(),
  ).find(int => int.id === currentIntegration);
  if (builtin) return builtin;
  if (plugin) return plugin;
  console.error(`Integration ${currentIntegration} does not exist`);
  return null;
};

export const integrationSetup = doWithIntegration('integrationSetup');
export const integrationCleanup = doWithIntegration('integrationCleanup');
export const cancelPayments = doWithIntegration('cancelPayments');
export const voidPayments = doWithIntegration('voidPayments');
export const initPayments = doWithIntegration(
  'initPayments',
  defaultInitPayment,
);
export const closeBatch = doWithIntegration('closeBatch', defaultCloseBatch);
